import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Content Review tab.
 * Lists all creator uploads awaiting admin approval. Parent (AdminPage) gets
 * notified of the current pending count via `onCountChange` so the sidebar
 * badge stays in sync.
 */
const ContentReviewTab = ({ onCountChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/content/pending`, { withCredentials: true });
      const list = res.data.items || [];
      setItems(list);
      if (onCountChange) onCountChange(res.data.total || list.length);
    } catch {
      console.error('Failed to fetch pending content');
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/api/admin/content/${id}/approve`, {}, { withCredentials: true });
      toast.success('Content approved and now live');
      fetchPending();
    } catch {
      toast.error('Failed to approve');
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Rejection reason:', 'Policy violation');
    if (reason === null) return;
    try {
      await axios.post(
        `${API}/api/admin/content/${id}/reject?reason=${encodeURIComponent(reason)}`,
        {}, { withCredentials: true },
      );
      toast.success('Content rejected');
      fetchPending();
    } catch {
      toast.error('Failed to reject');
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading review queue...</div>;

  return (
    <div data-testid="content-review-tab-content">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Content Review Queue</h2>
          <p className="text-slate-400 text-sm">All creator uploads require admin approval before going live</p>
        </div>
        <button
          onClick={fetchPending}
          className="px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
        >
          Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <div className="glass-light rounded-2xl p-12 text-center">
          <CheckCircle weight="fill" className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Queue Empty</h3>
          <p className="text-slate-400">No content pending review. All caught up!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="glass-light rounded-2xl overflow-hidden" data-testid={`review-item-${item.id}`}>
              {item.media_url && (
                <div className="aspect-video bg-slate-900 relative">
                  {item.content_type === 'video' ? (
                    <video src={`${API}${item.media_url}`} className="w-full h-full object-cover" />
                  ) : (
                    <img src={`${API}${item.media_url}`} alt={item.title} className="w-full h-full object-cover" />
                  )}
                  <span className="absolute top-2 left-2 px-2 py-1 bg-amber-500/80 text-amber-950 text-xs font-bold rounded-lg">PENDING REVIEW</span>
                </div>
              )}
              <div className="p-4">
                <h3 className="text-white font-semibold mb-1 truncate">{item.title}</h3>
                <p className="text-slate-400 text-sm mb-1">By: {item.creator_name}</p>
                <p className="text-slate-500 text-xs mb-3">
                  {item.content_type} &middot; {item.price_tokens > 0 ? `${item.price_tokens} tokens` : 'Free'} &middot; {new Date(item.created_at).toLocaleDateString()}
                </p>
                {item.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {item.tags.map(tag => <span key={tag} className="px-2 py-0.5 bg-slate-800 text-slate-400 rounded text-xs">{tag}</span>)}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => approve(item.id)}
                    className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors text-sm"
                    data-testid={`approve-${item.id}`}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => reject(item.id)}
                    className="flex-1 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg font-medium hover:bg-red-500/30 transition-colors text-sm"
                    data-testid={`reject-${item.id}`}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentReviewTab;
